import { supabase } from '../../config/supabase';

/**
 * Get Australian date
 */
const getAustralianDate = () => {
    return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
};

/**
 * Helper function to get week and day from date
 */
const getWeekAndDay = (dateString) => {
    const targetDate = new Date(dateString);
    const today = getAustralianDate();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    monday.setHours(0, 0, 0, 0);

    const diffTime = targetDate - monday;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(diffDays / 7) + 1;
    const dayOfWeek = targetDate.getDay();

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const day = dayNames[dayOfWeek];

    // Only return if within 4 weeks and valid day
    if (weekNumber >= 1 && weekNumber <= 4 && dayOfWeek >= 1 && dayOfWeek <= 6) {
        return {
            week: `week${weekNumber}`,
            day: day
        };
    }

    return null;
};

/**
 * Fetch scheduled assignments for a specific week and schedule type
 */
export const getScheduledAssignments = async (companyId, scheduleType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Apply schedule type specific filters
        if (scheduleType === 'editor') {
            query = query.not('expected_estimation_finish_date', 'is', null);
        } else if (scheduleType === 'production') {
            query = query.not('booking_delivery_date', 'is', null);
        } else if (scheduleType === 'delivery') {
            // For delivery: must have a booking date AND be ready_for_delivery status
            query = query
                .not('booking_delivery_date', 'is', null)
                .eq('main_status', 'ready_for_delivery');
        } else {
            // For specific quote types (roof, wall, floor)
            query = query.not('expected_estimation_finish_date', 'is', null);
        }

        // Apply user filters based on schedule type
        if (filters.userId) {
            if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
                // For design schedules, check if user is assigned as designer
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    // No jobs found for this user
                    return {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    };
                }
            } else if (scheduleType === 'production') {
                // For production, check production assignments
                const { data: prodJobs } = await supabase
                    .from('job_production')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = prodJobs?.map(p => p.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    };
                }
            } else if (scheduleType === 'delivery') {
                // For delivery, filter by assigned driver
                const { data: deliveryJobs } = await supabase
                    .from('job_deliveries')
                    .select('job_id')
                    .eq('assigned_driver_id', filters.userId);

                const jobIds = deliveryJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    };
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Organize data by weeks and days
        const scheduleData = {
            week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
        };

        // Process and categorize jobs
        data?.forEach(job => {
            const targetDate = scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)
                ? job.expected_estimation_finish_date
                : job.booking_delivery_date;

            if (targetDate) {
                const weekDay = getWeekAndDay(targetDate);
                if (weekDay && scheduleData[weekDay.week] && scheduleData[weekDay.week][weekDay.day]) {
                    scheduleData[weekDay.week][weekDay.day].push(transformJobData(job));
                }
            }
        });

        return scheduleData;
    } catch (error) {
        console.error('Error fetching scheduled assignments:', error);
        throw error;
    }
};

/**
 * Fetch unscheduled assignments
 */
export const getUnscheduledAssignments = async (companyId, scheduleType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Filter for unscheduled based on schedule type
        if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
            query = query.is('expected_estimation_finish_date', null);
        } else if (scheduleType === 'production') {
            query = query.is('booking_delivery_date', null);
        } else if (scheduleType === 'delivery') {
            // For delivery: must be ready_for_delivery AND no booking date set
            query = query
                .eq('main_status', 'ready_for_delivery')
                .is('booking_delivery_date', null);
        }

        // Apply user filters
        if (filters.userId) {
            if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return [];
                }
            } else if (scheduleType === 'production') {
                const { data: prodJobs } = await supabase
                    .from('job_production')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = prodJobs?.map(p => p.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return [];
                }
            } else if (scheduleType === 'delivery') {
                // For delivery, filter by assigned driver
                const { data: deliveryJobs } = await supabase
                    .from('job_deliveries')
                    .select('job_id')
                    .eq('assigned_driver_id', filters.userId);

                const jobIds = deliveryJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return [];
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        return data?.map(transformJobData) || [];
    } catch (error) {
        console.error('Error fetching unscheduled assignments:', error);
        throw error;
    }
};

// Add this function if it doesn't exist or update it
export const getEditorTableData = async (companyId, scheduleType, page = 1, limit = 20, filters = {}) => {
    try {
        const offset = (page - 1) * limit;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `, { count: 'exact' })
            .eq('company_id', companyId)
            .eq('deleted', false)
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);

        // Apply user filters
        if (filters.userId) {
            if (scheduleType === 'editor' || ['roof', 'wall', 'floor'].includes(scheduleType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        data: [],
                        pagination: { page, limit, total: 0, hasMore: false }
                    };
                }
            } else if (scheduleType === 'production') {
                const { data: prodJobs } = await supabase
                    .from('job_production')
                    .select('job_id')
                    .eq('user_id', filters.userId);

                const jobIds = prodJobs?.map(p => p.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return {
                        data: [],
                        pagination: { page, limit, total: 0, hasMore: false }
                    };
                }
            }
        }

        const { data, error, count } = await query;

        if (error) throw error;

        return {
            data: data?.map(transformJobData) || [],
            pagination: {
                page,
                limit,
                total: count || 0,
                hasMore: count ? (page * limit) < count : false
            }
        };
    } catch (error) {
        console.error('Error fetching editor table data:', error);
        throw error;
    }
};

/**
 * Transform job data from snake_case to camelCase
 */
const transformJobData = (job) => {
    // Transform quotes
    const quotes = {};
    if (job.job_quotes) {
        job.job_quotes.forEach(quote => {
            // Get designers for this quote type
            const designers = job.job_designers
                ?.filter(d => d.quote_type === quote.quote_type)
                ?.map(d => ({
                    userId: d.user_id,
                    name: d.name,
                    isStarted: d.is_started,
                    isCompleted: d.is_completed
                })) || [];

            quotes[quote.quote_type] = {
                isRequired: quote.is_required,
                designStatus: quote.design_status,
                estimationStatus: quote.estimation_status,
                designers: designers,
                production: job.job_production?.find(p => p.quote_type === quote.quote_type) ? {
                    phase: job.job_production.find(p => p.quote_type === quote.quote_type).phase,
                    userId: job.job_production.find(p => p.quote_type === quote.quote_type).user_id,
                    name: job.job_production.find(p => p.quote_type === quote.quote_type).name,
                    isStarted: job.job_production.find(p => p.quote_type === quote.quote_type).is_started_production,
                    isCompleted: job.job_production.find(p => p.quote_type === quote.quote_type).is_completed,
                    startCuttingAt: job.job_production.find(p => p.quote_type === quote.quote_type).start_cutting_at,
                    endCuttingAt: job.job_production.find(p => p.quote_type === quote.quote_type).end_cutting_at,
                    startProductionAt: job.job_production.find(p => p.quote_type === quote.quote_type).start_production_at,
                    endProductionAt: job.job_production.find(p => p.quote_type === quote.quote_type).end_production_at,
                } : null,
                delivery: job.job_deliveries?.find(d => d.quote_type === quote.quote_type) ? {
                    isRequired: job.job_deliveries.find(d => d.quote_type === quote.quote_type).is_required,
                    deliveryStatus: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_status,
                    deliveryDate: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_date,
                    assignedDriver: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_id ? {
                        driverId: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_id,
                        driverName: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_name,
                        assignedAt: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_at,
                        assignedBy: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_by_name ? {
                            name: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_by_name
                        } : null
                    } : null,
                    deliveryNotes: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_notes
                } : null
            };
        });
    }

    // Transform estimator
    const estimator = job.job_estimators?.[0] ? {
        userId: job.job_estimators[0].user_id,
        name: job.job_estimators[0].name,
        isCompleted: job.job_estimators[0].is_completed,
        isStarted: job.job_estimators[0].is_started
    } : null;

    return {
        _id: job.id,
        uniqueId: job.unique_id,
        customerName: job.customer_name,
        customerNumber: job.customer_number,
        customerMail: job.customer_mail,
        carpenterSupervisorName: job.carpenter_supervisor_name,
        customerAddress: job.customer_address,
        jobType: job.job_type,
        mainStatus: job.main_status,
        currentStatus: job.current_status,
        estimationFinishDate: job.expected_estimation_finish_date,
        bookingDeliveryDate: job.booking_delivery_date,
        invoiced: job.invoiced,
        jobInformation: {
            urgentQuote: job.urgent_quote,
            urgentDesign: job.urgent_design
        },
        quotes: quotes,
        estimator: estimator,
        createdAt: job.created_at,
        updatedAt: job.updated_at
    };
};

/**
 * Update job schedule date
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type
 * @param {Date|null} newDate - The new date or null to unschedule
 * @returns {Promise<Object>} Result of the update
 */
export const updateJobSchedule = async (jobId, quoteType, newDate) => {
    try {
        const updateData = {
            expected_estimation_finish_date: newDate ? newDate.toISOString() : null,
            updated_at: new Date().toISOString()
        };

        const { data, error } = await supabase
            .from('jobs')
            .update(updateData)
            .eq('id', jobId)
            .select() // Add select() to return the updated row
            .single(); // Expect a single row

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating job schedule:', error);
        return { data: null, error };
    }
};

/**
 * Fetch available users (designers and estimators)
 */
export const getAvailableUsers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, role')
            .eq('company_id', companyId)
            .in('role', ['designer', 'estimator'])
            .eq('status', 'active');

        if (error) throw error;

        // Organize by role
        const designers = data?.filter(u => u.role === 'designer').map(u => ({
            userId: u.id,
            username: u.username || u.full_name
        })) || [];

        const estimators = data?.filter(u => u.role === 'estimator').map(u => ({
            userId: u.id,
            username: u.username || u.full_name
        })) || [];

        return { designers, estimators };
    } catch (error) {
        console.error('Error fetching available users:', error);
        throw error;
    }
};

/**
 * Get production table data with pagination and filtering
 * @param {string} companyId - The company ID
 * @param {string} scheduleType - Type of schedule (production, editor, etc.)
 * @param {number} page - Current page number
 * @param {number} limit - Items per page
 * @param {Object} filters - Filter options (userId, etc.)
 * @returns {Promise<Object>} Production data and pagination info
 */
export const getProductionTableData = async (companyId, scheduleType, page = 1, limit = 20, filters = {}) => {
    try {
        const offset = (page - 1) * limit;

        // Base query for jobs with completed design quotes (ready for production)
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner (
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    estimation_value,
                    estimation_notes,
                    expected_finish_date
                ),
                job_production (
                    id,
                    quote_type,
                    user_id,
                    name,
                    phase,
                    is_started_cutting,
                    is_started_production,
                    is_completed,
                    start_cutting_at,
                    end_cutting_at,
                    start_production_at,
                    end_production_at,
                    production_notes
                ),
                job_designers (
                    id,
                    quote_type,
                    user_id,
                    name,
                    assigned_at,
                    started_at,
                    completed_at,
                    is_started,
                    is_completed,
                    finishing_note
                ),
                job_estimators (
                    id,
                    user_id,
                    name,
                    assigned_at,
                    started_at,
                    completed_at,
                    is_started,
                    is_completed,
                    finishing_note
                ),
                job_deliveries (
                    id,
                    quote_type,
                    is_required,
                    delivery_date,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name
                )
            `, { count: 'exact' })
            .eq('company_id', companyId)
            .eq('deleted', false)
            .eq('job_quotes.is_required', true)
            .in('main_status', ['pending_cutting', "in_cutting", "pending_production", "in_production"])
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);

        // Apply user filter if provided
        if (filters.userId) {
            // This will need to be refined based on how you want to filter
            // For now, filtering by designer or estimator
            query = query.or(`job_designers.user_id.eq.${filters.userId},job_estimators.user_id.eq.${filters.userId}`);
        }

        const { data, error, count } = await query;

        if (error) throw error;

        // Transform the data to match the expected format
        const transformedData = data.map(job => {
            // Group quotes by type
            const quotes = {};
            job.job_quotes.forEach(quote => {
                const quoteType = quote.quote_type;

                // Get designers for this quote type
                const designers = job.job_designers
                    .filter(d => d.quote_type === quoteType)
                    .map(d => ({
                        userId: d.user_id,
                        name: d.name,
                        assignedAt: d.assigned_at,
                        startedAt: d.started_at,
                        completedAt: d.completed_at,
                        isStarted: d.is_started,
                        isCompleted: d.is_completed,
                        finishingNote: d.finishing_note
                    }));

                // Get production data for this quote type
                const production = job.job_production.find(p => p.quote_type === quoteType);

                quotes[quoteType] = {
                    isRequired: quote.is_required,
                    designStatus: quote.design_status,
                    estimationStatus: quote.estimation_status,
                    estimationValue: quote.estimation_value,
                    estimationNotes: quote.estimation_notes,
                    expectedFinishDate: quote.expected_finish_date,
                    designers: designers,
                    production: production ? {
                        userId: production.user_id,
                        name: production.name,
                        phase: production.phase,
                        isStartedCutting: production.is_started_cutting,
                        isStartedProduction: production.is_started_production,
                        isCompleted: production.is_completed,
                        startCuttingAt: production.start_cutting_at,
                        endCuttingAt: production.end_cutting_at,
                        startProductionAt: production.start_production_at,
                        endProductionAt: production.end_production_at,
                        productionNotes: production.production_notes
                    } : null
                };
            });




            // Get estimator
            const estimator = job?.job_estimators?.[0] ? {
                userId: job.job_estimators[0].user_id,
                name: job.job_estimators[0].name,
                assignedAt: job.job_estimators[0].assigned_at,
                startedAt: job.job_estimators[0].started_at,
                completedAt: job.job_estimators[0].completed_at,
                isStarted: job.job_estimators[0].is_started,
                isCompleted: job.job_estimators[0].is_completed,
                finishingNote: job.job_estimators[0].finishing_note
            } : null;


            return {
                _id: job.id,
                uniqueId: job.unique_id,
                customerName: job.customer_name,
                customerNumber: job.customer_number,
                customerMail: job.customer_mail,
                carpenterSupervisorName: job.carpenter_supervisor_name,
                carpenterSupervisorNumber: job.carpenter_supervisor_number,
                customerAddress: job.customer_address,
                jobType: job.job_type,
                mainStatus: job.main_status,
                currentStatus: job.current_status,
                invoiced: job.invoiced,
                bookingDeliveryDate: job.booking_delivery_date,
                expectedEstimationFinishDate: job.expected_estimation_finish_date,
                createdAt: job.created_at,
                updatedAt: job.updated_at,
                jobInformation: {
                    roofType: job.roof_type,
                    roofTrussCentres: job.roof_truss_centres,
                    isSingleStorey: job.is_single_storey,
                    isUnit: job.is_unit,
                    unitCount: job.unit_count,
                    urgentQuote: job.urgent_quote,
                    urgentDesign: job.urgent_design,
                    siteMeasureRequired: job.site_measure_required,
                    siteMeasureWall: job.site_measure_wall,
                    siteMeasureFloor: job.site_measure_floor,
                    siteMeasureTruss: job.site_measure_truss
                },
                quotes: quotes,
                estimator: estimator,
                timberJob: false // Set based on your timber logic
            };
        });

        return {
            data: transformedData,
            pagination: {
                page,
                limit,
                total: count || 0,
                hasMore: (offset + limit) < (count || 0)
            }
        };
    } catch (error) {
        console.error('Error fetching production table data:', error);
        throw error;
    }
};

/**
 * Fetch scheduled assignments organized by week and day
 */
export const getScheduleAssignments = async (companyId, quoteType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Filter by quote type and ensure it's required
        if (quoteType !== 'delivery') {
            query = query
                .eq('job_quotes.quote_type', quoteType)
                .eq('job_quotes.is_required', true);
        }

        // Get only scheduled assignments (has expected_finish_date)
        if (quoteType === 'delivery') {
            query = query.not('booking_delivery_date', 'is', null);
        } else {
            query = query.not('job_quotes.expected_finish_date', 'is', null);
        }

        // Apply user filters
        if (filters.userId) {
            if (['roof', 'wall', 'floor'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: { week1: {}, week2: {}, week3: {}, week4: {} }, error: null };
                }
            }
        } else if (filters.showOnlyMine) {
            if (['roof', 'wall', 'floor'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.showOnlyMine)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: { week1: {}, week2: {}, week3: {}, week4: {} }, error: null };
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Transform and organize by week and day
        const organized = {
            week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
        };

        const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);
        monday.setHours(0, 0, 0, 0);

        data?.forEach(job => {
            let dateToUse;
            let quote = null;

            if (quoteType === 'delivery') {
                dateToUse = job.booking_delivery_date;
                quote = { quote_type: 'delivery' }; // Mock quote for delivery
            } else {
                // Find the specific quote for this type
                quote = job.job_quotes?.find(q => q.quote_type === quoteType);
                if (!quote?.expected_finish_date) return;
                dateToUse = quote.expected_finish_date;
            }

            if (!dateToUse) return;

            // Filter designers for this quote type (only for non-delivery)
            const designers = quoteType !== 'delivery'
                ? job.job_designers?.filter(d => d.quote_type === quoteType) || []
                : [];

            // Filter production for this quote type (only for non-delivery)
            const production = quoteType !== 'delivery'
                ? job.job_production?.find(p => p.quote_type === quoteType)
                : null;

            // Filter delivery for this quote type
            const delivery = job.job_deliveries?.find(d => d.quote_type === quoteType);

            // Transform the job data
            const transformedJob = {
                ...job,
                // Add quote-specific data
                quote_info: quote,
                designers: designers,
                production: production,
                delivery: delivery
            };

            const jobDate = new Date(dateToUse); // Use dateToUse instead of quote.expected_finish_date
            const diffDays = Math.floor((jobDate - monday) / (1000 * 60 * 60 * 24));

            if (diffDays >= 0 && diffDays < 28) {
                const weekNum = Math.floor(diffDays / 7) + 1;
                const dayOfWeek = jobDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Australia/Melbourne' });

                if (organized[`week${weekNum}`] && organized[`week${weekNum}`][dayOfWeek]) {
                    organized[`week${weekNum}`][dayOfWeek].push(transformedJob);
                }
            }
        });

        return { data: organized, error: null };
    } catch (error) {
        console.error('Error fetching scheduled assignments:', error);
        return { data: { week1: {}, week2: {}, week3: {}, week4: {} }, error };
    }
};



/**
 * Fetch unscheduled assignments for a specific quote type
 */
export const getUnscheduledByQuote = async (companyId, quoteType, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_finish_date
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Filter by quote type and ensure it's required
        if (quoteType !== 'delivery') {
            query = query
                .eq('job_quotes.quote_type', quoteType)
                .eq('job_quotes.is_required', true);
        }

        // Get only unscheduled assignments (no expected_finish_date)
        if (quoteType === 'delivery') {
            query = query
                .eq('main_status', 'ready_for_delivery')
                .is('booking_delivery_date', null);
        } else {
            query = query.is('job_quotes.expected_finish_date', null);
        }

        // Apply user filters
        if (filters.userId) {
            if (['roof', 'wall', 'floor'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.userId)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: [], error: null };
                }
            }
        } else if (filters.showOnlyMine) {
            if (['roof', 'wall', 'floor'].includes(quoteType)) {
                const { data: designerJobs } = await supabase
                    .from('job_designers')
                    .select('job_id')
                    .eq('user_id', filters.showOnlyMine)
                    .eq('quote_type', quoteType);

                const jobIds = designerJobs?.map(d => d.job_id) || [];
                if (jobIds.length > 0) {
                    query = query.in('id', jobIds);
                } else {
                    return { data: [], error: null };
                }
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // DON'T transform - return raw data just like getScheduleAssignments does before organizing
        // Just filter to include quote-specific data
        const processedData = data?.map(job => {

            if (quoteType === 'delivery') {
                return {
                    ...job,
                    quote_info: { quote_type: 'delivery' }, // Mock quote info for delivery
                    designers: [],
                    production: null,
                    delivery: job.job_deliveries?.[0] || null
                };
            }


            // Find the specific quote for this type
            const quote = job.job_quotes?.find(q => q.quote_type === quoteType);

            // Filter designers for this quote type
            const designers = job.job_designers?.filter(d => d.quote_type === quoteType) || [];

            // Filter production for this quote type
            const production = job.job_production?.find(p => p.quote_type === quoteType);

            // Filter delivery for this quote type
            const delivery = job.job_deliveries?.find(d => d.quote_type === quoteType);

            return {
                ...job, // Keep raw job data (snake_case)
                // Add quote-specific data
                quote_info: quote,
                designers: designers,
                production: production,
                delivery: delivery
            };
        }) || [];

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching unscheduled assignments:', error);
        return { data: [], error };
    }
};


/**
 * Update job quote expected finish date or delivery booking date
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type (roof, wall, floor, delivery)
 * @param {Date|null} newDate - The new date or null to unschedule
 * @returns {Promise<Object>} Result of the update
 */
export const updateFinishDate = async (jobId, quoteType, newDate) => {
    try {
        const updateData = {
            updated_at: new Date().toISOString()
        };

        let data, error;

        if (quoteType === 'delivery') {
            // Update the jobs table booking_delivery_date for delivery
            updateData.booking_delivery_date = newDate ? newDate.toISOString() : null;

            const result = await supabase
                .from('jobs')
                .update(updateData)
                .eq('id', jobId)
                .select()
                .single();

            data = result.data;
            error = result.error;
        } else {
            // Update the job_quotes table for other quote types
            updateData.expected_finish_date = newDate ? newDate.toISOString() : null;

            const result = await supabase
                .from('job_quotes')
                .update(updateData)
                .eq('job_id', jobId)
                .eq('quote_type', quoteType)
                .select()
                .single();

            data = result.data;
            error = result.error;
        }

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating finish date:', error);
        return { data: null, error };
    }
};


/**
 * Fetch scheduled delivery assignments organized by week and day
 * NOW RETURNS QUOTE-BASED CARDS: If a job has 2 required quotes, it will appear as 2 separate cards
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options (userId, showOnlyMine)
 * @returns {Promise<Object>} Organized delivery data by week and day (one card per quote)
 */
export const getScheduledDeliveries = async (companyId, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_delivery_date
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .in('main_status', ['ready_for_delivery', 'delivery_scheduled', 'in_delivery']);

        // Apply user filters for delivery (filter by assigned driver)
        if (filters.userId) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.userId);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return {
                    data: {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    },
                    error: null
                };
            }
        } else if (filters.showOnlyMine) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.showOnlyMine);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return {
                    data: {
                        week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                        week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
                    },
                    error: null
                };
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Organize by week and day
        const organized = {
            week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
            week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
        };

        const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);
        monday.setHours(0, 0, 0, 0);

        // NEW LOGIC: Create one card per quote instead of per job
        data?.forEach(job => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required) || [];

            requiredQuotes.forEach(quote => {
                // Only show if quote has BOTH expected_delivery_date AND assigned driver
                const delivery = job.job_deliveries?.find(d => d.quote_type === quote.quote_type);

                if (!quote.expected_delivery_date || !delivery?.assigned_driver_id) {
                    return; // Skip this quote if it doesn't have both date and driver
                }

                const transformedQuoteCard = {
                    ...job,
                    quote_info: {
                        quote_type: quote.quote_type,
                        expected_finish_date: quote.expected_delivery_date,
                        id: quote.id
                    },
                    designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                    production: job.job_production?.find(p => p.quote_type === quote.quote_type) || null,
                    delivery: delivery
                };

                const quoteDate = new Date(quote.expected_delivery_date);
                const diffDays = Math.floor((quoteDate - monday) / (1000 * 60 * 60 * 24));

                if (diffDays >= 0 && diffDays < 28) {
                    const weekNum = Math.floor(diffDays / 7) + 1;
                    const dayOfWeek = quoteDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Australia/Melbourne' });

                    if (organized[`week${weekNum}`] && organized[`week${weekNum}`][dayOfWeek]) {
                        organized[`week${weekNum}`][dayOfWeek].push(transformedQuoteCard);
                    }
                }
            });
        });

        return { data: organized, error: null };
    } catch (error) {
        console.error('Error fetching scheduled deliveries:', error);
        return {
            data: {
                week1: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                week2: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                week3: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] },
                week4: { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [] }
            },
            error
        };
    }
};

/**
 * Fetch unscheduled delivery assignments
 * NOW RETURNS QUOTE-BASED CARDS: Creates one card per required quote
 * A quote appears in unscheduled if it's missing EITHER expected_delivery_date OR assigned driver
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options (userId, showOnlyMine)
 * @returns {Promise<Object>} Unscheduled delivery data (one card per quote)
 */
export const getUnscheduledDeliveries = async (companyId, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_delivery_date
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_production(
                    id,
                    quote_type,
                    phase,
                    is_completed
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .in('main_status', ['ready_for_delivery', 'delivery_scheduled', 'in_delivery']);

        // Apply user filters for delivery (filter by assigned driver)
        if (filters.userId) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.userId);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        } else if (filters.showOnlyMine) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.showOnlyMine);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // NEW LOGIC: Create one card per quote, but only for quotes missing date OR driver
        const processedData = [];
        data?.forEach(job => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required) || [];

            requiredQuotes.forEach(quote => {
                const delivery = job.job_deliveries?.find(d => d.quote_type === quote.quote_type);

                // Show in unscheduled if missing EITHER date OR driver (or both)
                if (!quote.expected_delivery_date || !delivery?.assigned_driver_id) {
                    const transformedQuoteCard = {
                        ...job,
                        quote_info: {
                            quote_type: quote.quote_type,
                            expected_finish_date: quote.expected_delivery_date || null,
                            id: quote.id
                        },
                        designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                        production: job.job_production?.find(p => p.quote_type === quote.quote_type) || null,
                        delivery: delivery || null
                    };

                    processedData.push(transformedQuoteCard);
                }
            });
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching unscheduled deliveries:', error);
        return { data: [], error };
    }
};

/**
 * Update delivery expected date for a specific quote
 * NOW UPDATES: job_quotes.expected_delivery_date instead of jobs.booking_delivery_date
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type (roof, wall, floor, timber)
 * @param {Date|null} newDate - The new date or null to unschedule
 * @returns {Promise<Object>} Result of the update
 */
export const updateDeliveryDate = async (jobId, quoteType, newDate) => {
    try {
        const updateData = {
            expected_delivery_date: newDate ? newDate.toISOString() : null,
            updated_at: new Date().toISOString()
        };

        const { data, error } = await supabase
            .from('job_quotes')
            .update(updateData)
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .select()
            .single();

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating delivery date:', error);
        return { data: null, error };
    }
};